import React, { useState } from "react";
import {
  Download,
  Filter,
  Activity,
  Zap,
  Droplets,
  Wrench,
  ArrowUpRight,
  ArrowDownRight,
  Search,
  LayoutDashboard,
  ShieldCheck,
  TrendingDown,
  Clock,
  CircleAlert,
  TextSelect,
} from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useSelector } from "react-redux";
import { TbBorderSides } from "react-icons/tb";

// --- Production Level Mock Data ---
const MOCK_DATA = {
  pumpPerformance: [
    {
      asset: "Pump-01 (KDI-75)",
      location: "T-30, Trikuta Nagar",
      flow: "125 m³/h",
      head: "45m",
      power: "55 kW",
      efficiency: "78%",
      status: "Optimal",
    },
    {
      asset: "Pump-02 (ABC-120)",
      location: "Narwal Station",
      flow: "110 m³/h",
      head: "42m",
      power: "62 kW",
      efficiency: "65%",
      status: "Warning",
    },
    {
      asset: "Pump-03 (Grundfos)",
      location: "Gandhi Nagar",
      flow: "140 m³/h",
      head: "48m",
      power: "58 kW",
      efficiency: "82%",
      status: "Optimal",
    },
  ],
  energyCost: [
    {
      site: "Trikuta Nagar Sump",
      kwh: "12,450",
      tariff: "₹7.50",
      cost: "₹93,375",
      costPerM3: "₹1.20",
    },
    {
      site: "Narwal Pumping Station",
      kwh: "18,200",
      tariff: "₹7.50",
      cost: "₹136,500",
      costPerM3: "₹1.45",
    },
    {
      site: "South Zone Reservoir",
      kwh: "8,900",
      tariff: "₹7.50",
      cost: "₹66,750",
      costPerM3: "₹1.15",
    },
  ],
  nrwDiagnostic: [
    {
      dma: "DMA-01 (Narwal)",
      input: "5,000 m³",
      billed: "3,800 m³",
      nrw: "24%",
      pressure: "3.2 bar",
      nightFlow: "12 m³/h",
    },
    {
      dma: "DMA-02 (Gandhi)",
      input: "4,200 m³",
      billed: "2,900 m³",
      nrw: "31%",
      pressure: "2.8 bar",
      nightFlow: "18 m³/h",
    },
    {
      dma: "DMA-03 (South-1)",
      input: "6,100 m³",
      billed: "5,200 m³",
      nrw: "14.7%",
      pressure: "3.5 bar",
      nightFlow: "8 m³/h",
    },
  ],
  maintenance: [
    {
      asset: "Pump-01",
      failures: "02",
      mtbf: "450 hrs",
      downtime: "12 hrs",
      cost: "₹15,400",
    },
    {
      asset: "Flowmeter-S",
      failures: "00",
      mtbf: "2,100 hrs",
      downtime: "0 hrs",
      cost: "₹0",
    },
    {
      asset: "PLC-Siemens",
      failures: "01",
      mtbf: "1,200 hrs",
      downtime: "4 hrs",
      cost: "₹8,200",
    },
  ],
};

const FlowReport = () => {
  const themeMode = useSelector((state) => state.theme.mode);
  const textColorClass = themeMode ? "dark_text-color" : "text-primary";
  const bgColorClass = themeMode ? "bg_light_main" : "bg-white";
  const borderThem = themeMode ? "border_them_2" : "border-primary";
  const tableHedBg = themeMode ? "table_hed_bg" : "tabel_hed_def_bg";
  const cardBgClass = themeMode ? "bg-slate-900/50" : "bg-white";

  const [activeReport, setActiveReport] = useState("pump");

  return (
    <div className={`w-full ${bgColorClass} ${textColorClass} min-h-screen`}>
      <div
        className={`py-4 md:px-12 px-6 border-b ${borderThem} flex justify-between items-center h-[57px]`}
      >
        <div className="flex items-center gap-x-2">
          <TextSelect  size={16} />
          <p className="font-semibold tracking-tight text-sm">
          Asset Flow Report
          </p>
        </div>
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase opacity-60">
          <span className="flex items-center gap-1">
            <ShieldCheck size={14} className="text-green-500" /> Data Verified
          </span>
          <span className="text-slate-500">|</span>
          <span>Last Sync: 2 Mins ago</span>
        </div>
      </div>

      <div className="h-[calc(100vh-57px)] pb-10 overflow-y-auto sidebar_scroll p-6 md:p-10">
        <div className="space-y-6">
          <div
            className={`${cardBgClass} p-4 rounded-2xl border ${borderThem} flex flex-col md:flex-row justify-between items-center gap-4`}
          >
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-slate-400">
                <Filter size={18} />
                <span className="text-[10px] font-black uppercase tracking-widest">
                  Filters
                </span>
              </div>
              <Select defaultValue="all-assets">
                <SelectTrigger
                  className={`w-[180px] ${tableHedBg} cursor-pointer ${borderThem} text-xs font-semibold h-9`}
                >
                  <SelectValue placeholder="All Assets" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all-assets">All Assets</SelectItem>
                  <SelectItem value="pumps">Heavy Pumps</SelectItem>
                  <SelectItem value="meters">Flowmeters</SelectItem>
                </SelectContent>
              </Select>
              <Select defaultValue="all-dmas">
                <SelectTrigger
                  className={`w-[180px]  ${borderThem} cursor-pointer ${tableHedBg} text-xs font-semibold h-9`}
                >
                  <SelectValue placeholder="All DMAs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all-dmas">All DMAs</SelectItem>
                  <SelectItem value="south">South Zone</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs px-6 h-9 rounded-lg flex items-center gap-2 shadow-lg shadow-blue-500/20">
              <Download size={14} /> EXPORT AUDIT (CSV)
            </Button>
          </div>

          {/* --- Main Report Container --- */}
          <Card
            className={`${cardBgClass} ${borderThem} rounded-3xl overflow-hidden`}
          >
            <CardHeader className="pb-0">
              <Tabs
                defaultValue="pump"
                onValueChange={setActiveReport}
                className="w-full"
              >
                <TabsList
                  className={`bg-transparent border-b ${borderThem} rounded-none w-full justify-start h-auto p-0 gap-10`}
                >
                  <ReportTabTrigger
                    textColorClass={textColorClass}
                    value="pump"
                    label="Pump Performance"
                    icon={<Activity size={14} />}
                  />
                  <ReportTabTrigger
                    textColorClass={textColorClass}
                    value="energy"
                    label="Energy Cost"
                    icon={<Zap size={14} />}
                  />
                  <ReportTabTrigger
                    textColorClass={textColorClass}
                    value="nrw"
                    label="NRW Diagnostic"
                    icon={<Droplets size={14} />}
                  />
                  <ReportTabTrigger
                    textColorClass={textColorClass}
                    value="maint"
                    label="Maintenance Effectiveness"
                    icon={<Wrench size={14} />}
                  />
                </TabsList>

                <div className="py-8">
                  <div className="flex justify-between items-end mb-6">
                    <div>
                      <h2
                        className={`text-base font-semibold ${textColorClass} tracking-tighter uppercase italic`}
                      >
                        {activeReport === "pump" && "Hydraulic Efficiency Log"}
                        {activeReport === "energy" &&
                          "Energy Consumption Audit"}
                        {activeReport === "nrw" && "NRW Strategic Diagnosis"}
                        {activeReport === "maint" &&
                          "Reliability & Cost Matrix"}
                      </h2>
                      <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-widest mt-1">
                        Live Operational Parameters vs Design Specifications
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                      <span className="text-[10px] font-black uppercase opacity-40">
                        Live Monitoring
                      </span>
                    </div>
                  </div>

                  {/* 1. Pump Performance Report */}
                  <TabsContent value="pump">
                    <div
                      className={`rounded-xl border ${borderThem} overflow-hidden`}
                    >
                      <Table>
                        <TableHeader className={`${tableHedBg}`}>
                          <TableRow
                            className={`${borderThem} hover:bg-transparent`}
                          >
                            <TableHead className="report-th">
                              Asset Details
                            </TableHead>
                            <TableHead className="report-th">
                              Location
                            </TableHead>
                            <TableHead className="report-th text-center">
                              Flow (m³/h)
                            </TableHead>
                            <TableHead className="report-th text-center">
                              Head (m)
                            </TableHead>
                            <TableHead className="report-th text-center">
                              Power (kW)
                            </TableHead>
                            <TableHead className="report-th text-center">
                              Efficiency (%)
                            </TableHead>
                            <TableHead className="report-th text-right">
                              Status
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {MOCK_DATA.pumpPerformance.map((row, i) => (
                            <TableRow key={i} className={`${borderThem}`}>
                              <TableCell className="font-semibold text-blue-400 tracking-tighter">
                                {row.asset}
                              </TableCell>
                              <TableCell className="text-slate-500 italic text-xs">
                                {row.location}
                              </TableCell>
                              <TableCell className="text-center font-semibold">
                                {row.flow}
                              </TableCell>
                              <TableCell className="text-center font-semibold">
                                {row.head}
                              </TableCell>
                              <TableCell className="text-center font-semibold text-yellow-500/80">
                                {row.power}
                              </TableCell>
                              <TableCell className="text-center font-mono font-black text-emerald-500">
                                {row.efficiency}
                              </TableCell>
                              <TableCell className="text-right">
                                <span
                                  className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase ${
                                    row.status === "Optimal"
                                      ? "bg-green-500/10 text-green-500"
                                      : "bg-red-500/10 text-red-500"
                                  }`}
                                >
                                  {row.status}
                                </span>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </TabsContent>

                  {/* 2. Energy Cost Report */}
                  <TabsContent value="energy">
                    <div
                      className={`rounded-xl border ${borderThem} overflow-hidden`}
                    >
                      <Table>
                        <TableHeader className={`${tableHedBg}`}>
                          <TableRow
                            className={`${borderThem} hover:bg-transparent`}
                          >
                            <TableHead className="report-th">
                              Asset / Site
                            </TableHead>
                            <TableHead className="report-th text-center">
                              kWh Usage
                            </TableHead>
                            <TableHead className="report-th text-center">
                              Tariff (Rate)
                            </TableHead>
                            <TableHead className="report-th text-center">
                              Total Cost
                            </TableHead>
                            <TableHead className="report-th text-right font-black">
                              Cost per m³
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {MOCK_DATA.energyCost.map((row, i) => (
                            <TableRow
                              key={i}
                              className={`${borderThem} hover:bg-yellow-500/5`}
                            >
                              <TableCell className="font-semibold">
                                {row.site}
                              </TableCell>
                              <TableCell className="text-center font-mono">
                                {row.kwh}
                              </TableCell>
                              <TableCell className="text-center opacity-60 font-semibold">
                                {row.tariff}
                              </TableCell>
                              <TableCell className="text-center font-black text-yellow-500">
                                {row.cost}
                              </TableCell>
                              <TableCell className="text-right font-mono text-blue-400 font-black">
                                {row.costPerM3}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </TabsContent>

                  {/* 3. NRW Diagnostic Report */}
                  <TabsContent value="nrw">
                    <div
                      className={`rounded-xl border ${borderThem} overflow-hidden`}
                    >
                      <Table>
                        <TableHeader className={`${tableHedBg}`}>
                          <TableRow
                            className={`${borderThem} hover:bg-transparent`}
                          >
                            <TableHead className="report-th">DMA</TableHead>
                            <TableHead className="report-th text-center">
                              System Input
                            </TableHead>
                            <TableHead className="report-th text-center">
                              Billed Volume
                            </TableHead>
                            <TableHead className="report-th text-center">
                              NRW %
                            </TableHead>
                            <TableHead className="report-th text-center">
                              Pressure
                            </TableHead>
                            <TableHead className="report-th text-right">
                              Night Flow
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {MOCK_DATA.nrwDiagnostic.map((row, i) => (
                            <TableRow
                              key={i}
                              className={`${borderThem} hover:bg-emerald-500/5`}
                            >
                              <TableCell className="font-semibold text-emerald-400">
                                {row.dma}
                              </TableCell>
                              <TableCell className="text-center font-semibold">
                                {row.input}
                              </TableCell>
                              <TableCell className="text-center font-semibold text-blue-400">
                                {row.billed}
                              </TableCell>
                              <TableCell className="text-center">
                                <div className="flex items-center justify-center gap-1 font-black">
                                  <span
                                    className={
                                      parseFloat(row.nrw) > 25
                                        ? "text-red-500"
                                        : "text-green-500"
                                    }
                                  >
                                    {row.nrw}
                                  </span>
                                  {parseFloat(row.nrw) > 25 ? (
                                    <ArrowUpRight
                                      size={14}
                                      className="text-red-500"
                                    />
                                  ) : (
                                    <ArrowDownRight
                                      size={14}
                                      className="text-green-500"
                                    />
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-center opacity-60 font-semibold">
                                {row.pressure}
                              </TableCell>
                              <TableCell className="text-right font-black text-orange-400 italic underline decoration-orange-400/20">
                                {row.nightFlow}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </TabsContent>

                  {/* 4. Maintenance Effectiveness Report */}
                  <TabsContent value="maint">
                    <div
                      className={`rounded-xl border ${borderThem} overflow-hidden`}
                    >
                      <Table>
                        <TableHeader className={`${tableHedBg}`}>
                          <TableRow
                            className={`${borderThem} hover:bg-transparent`}
                          >
                            <TableHead className="report-th">
                              Asset Name
                            </TableHead>
                            <TableHead className="report-th text-center">
                              Total Failures
                            </TableHead>
                            <TableHead className="report-th text-center">
                              MTBF Index
                            </TableHead>
                            <TableHead className="report-th text-center">
                              Downtime
                            </TableHead>
                            <TableHead className="report-th text-right">
                              Maintenance Cost
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {MOCK_DATA.maintenance.map((row, i) => (
                            <TableRow
                              key={i}
                              className={`${borderThem}  transition-all`}
                            >
                              <TableCell className="font-semibold uppercase tracking-tighter">
                                {row.asset}
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge
                                  variant="outline"
                                  className="text-red-500 border-red-500/20 bg-red-500/5"
                                >
                                  {row.failures}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center font-mono opacity-80">
                                {row.mtbf}
                              </TableCell>
                              <TableCell className="text-center font-semibold text-orange-500">
                                {row.downtime}
                              </TableCell>
                              <TableCell className="text-right font-semibold ">
                                {row.cost}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </TabsContent>
                </div>
              </Tabs>
            </CardHeader>
          </Card>
        </div>
      </div>

      {/* Styles for the TH to keep it dry */}
      <style>{`
        .report-th {
          padding: 1rem !important;
          color: #94a3b8 !important;
          font-weight: 900 !important;
          text-transform: uppercase !important;
          font-size: 10px !important;
          letter-spacing: 0.05em !important;
        }
      `}</style>
    </div>
  );
};

// Reusable Tab Trigger Component for cleaner UI
const ReportTabTrigger = ({ value, label, icon, textColorClass }) => (
  <TabsTrigger
    value={value}
    className={`data-[state=active]:bg-transparent data-[state=active]:text-blue-500 data-[state=active]:border-b-2 data-[state=active]:border-blue-500 rounded-none py-3 cursor-pointer px-0 font-black uppercase tracking-[0.1em] text-[11px] flex items-center gap-2 opacity-50 data-[state=active]:opacity-100 ${textColorClass} transition-all`}
  >
    {icon} {label}
  </TabsTrigger>
);

export default FlowReport;
